import type { DateMessage } from '@getstep/proto/common'
import { formatDuration, intervalToDuration } from 'date-fns'
import { addDays } from 'date-fns/addDays'
import { addMinutes } from 'date-fns/addMinutes'
import { format } from 'date-fns/format'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { getDate } from 'date-fns/getDate'
import { isFuture } from 'date-fns/isFuture'
import { isThisYear } from 'date-fns/isThisYear'
import { isValid } from 'date-fns/isValid'
import { subMinutes } from 'date-fns/subMinutes'

import { TEXT } from './text'

/**
 * Formats a date to a December 31, 2012 string
 * omits year and shortens the month, if same year as now, Dec 12th
 */
export function formatRelativeDate(date: Date | number): string {
    return isThisYear(date)
        ? formatDate(date, 'MMM do')
        : formatDate(date, 'MMM yyyy')
}

/**
 * Formats a date into "Expires at ..." string,
 * returns nothing if date is not in the future
 */
export function formatExpiresAt(date: Date | number): string {
    if (!isFuture(date)) return ''
    return formatDistanceToNow(date, {
        addSuffix: true,
        includeSeconds: true,
    })
}

export function formatDate(date: Date | number, fmt: DateFormat) {
    return format(date, fmt)
}

export function formatSecondsAsDuration(duration: string): string {
    const seconds = parseInt(duration.replace('s', ''))
    const interval = intervalToDuration({ start: 0, end: seconds * 1000 })

    return formatDuration(interval, { format: ['days', 'hours', 'minutes'] })
}

export const dateFromProtoDate = (d: DateMessage) => {
    // CommonProto.Date.month is 1 indexed while Date.month is 0 indexed.
    return new Date(d.year, d.month - 1, d.day)
}

export function isValidDate(date: unknown): date is Date {
    return isValid(date)
}

/**
 * Adds the timezone offset to the date
 * @param date
 * @returns
 */
export const formatTimezone = (date: Date) => {
    const offset = date.getTimezoneOffset()

    return Math.sign(offset) !== -1
        ? addMinutes(date, offset)
        : subMinutes(date, Math.abs(offset))
}

type DateFormat =
    | 'MMMM d, yyyy'
    | 'MMM do'
    | 'MMM yyyy'
    | 'MM / dd / yyyy'
    | 'MM / yy'
/**
 * Returns the day of the week name, 0 indexed
 */
export const getDayOfWeekName = (day: number) => {
    const days = [
        TEXT.schedule.day.sunday,
        TEXT.schedule.day.monday,
        TEXT.schedule.day.tuesday,
        TEXT.schedule.day.wednesday,
        TEXT.schedule.day.thursday,
        TEXT.schedule.day.friday,
        TEXT.schedule.day.saturday,
    ]
    return days[day]
}

/**
 * Calculates the trial end date by adding the specified number of days to today
 * @param trialDurationDays - Number of days in the trial period
 * @returns Formatted date string (e.g., "January 15, 2025")
 */
export const calculateTrialEndDate = (trialDurationDays: number): string => {
    const endDate = addDays(new Date(), trialDurationDays)
    return formatDate(endDate, 'MMMM d, yyyy')
}

/**
 * Formats a day number with its ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
 * @param day - Day of the month (1-31)
 * @returns Day with ordinal suffix (e.g., "1st", "2nd", "3rd", "21st")
 */
export const formatDayWithOrdinal = (day: number): string => {
    if (day > 3 && day < 21) return day + 'th'

    switch (day % 10) {
        case 1:
            return day + 'st'
        case 2:
            return day + 'nd'
        case 3:
            return day + 'rd'
        default:
            return day + 'th'
    }
}

/**
 * Formats a js-joda LocalDateTime as "Month Dayth" (e.g., "November 18th")
 * @param jsJodaDate - js-joda LocalDateTime object
 * @returns Formatted date string
 */
export const formatJsJodaDateWithOrdinal = (jsJodaDate: any): string => {
    // Convert js-joda LocalDateTime to JavaScript Date
    const jsDate = new Date(
        jsJodaDate.year(),
        jsJodaDate.monthValue() - 1,
        jsJodaDate.dayOfMonth(),
    )

    // Format month name using date-fns
    const month = format(jsDate, 'MMMM')
    const day = getDate(jsDate)

    return `${month} ${formatDayWithOrdinal(day)}`
}
